import { DotsThreeVertical, Info, WarningCircle } from "@phosphor-icons/react";
import clsx from "clsx";

import IconAvatar from "common/components/IconAvatar";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { timeSince } from "common/helpers/utils";

import { LEAD_LIST_SOURCE_TYPES } from "../../constants";
import useSourceResultsTotal from "../../datahooks/useSourceResultsTotal";
import { isActionsElementClick } from "../../utils";
import SourceActions from "./SourceActions";
import SourceProgress from "./SourceProgress";

import type { LeadListSource } from "../../types";

interface SourceItemProps {
  listId: string;
  source: LeadListSource;
  isSelected: boolean;
  selectSource: (sourceId: string) => void;
}

export default function SourceItem({
  listId,
  source,
  isSelected,
  selectSource,
}: SourceItemProps) {
  const { processed, total, type, state, last_mined_at: lastMinedAt } = source;

  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { workspaceAccounts } = useWorkspaceAccounts(workspaceId);

  const minerAccount = workspaceAccounts?.find(
    ({ id }) => "miner" in source && id === source.miner,
  );
  const isDisconnected = minerAccount
    ? minerAccount.state !== "LoggedIn"
    : false;

  const {
    icon: Icon,
    label,
    iconContainerClassName,
  } = LEAD_LIST_SOURCE_TYPES[type];

  const hasIncompleteLeads = state === "COMPLETED" && processed < total;

  const { total: resultsTotal, isLoadingTotal } = useSourceResultsTotal({
    listId,
    sourceId: source.id,
    enabled: state === "COMPLETED",
  });

  return (
    /** biome-ignore lint/a11y/useSemanticElements: this "button" has to be a div since it contains a button element */
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (isActionsElementClick(e.target)) return;
        selectSource(source.id);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          selectSource(source.id);
        }
      }}
      className={clsx([
        "w-[240px] shrink-0 relative overflow-hidden group hover:shadow-sm transition-shadow duration-300 p-4 bg-black-50 rounded-4xl flex flex-row items-center gap-3 cursor-pointer border",
        isSelected ? "border-purple-500" : "border-transparent",
      ])}
    >
      <IconAvatar className={iconContainerClassName}>
        <Icon />
      </IconAvatar>

      <div
        className={clsx([
          "flex flex-col w-full",
          state === "COMPLETED" ? "gap-0.5" : "gap-2",
        ])}
      >
        <span className="flex flex-row gap-1 items-center text-xs-bold">
          <span className="text-nowrap truncate max-w-[12ch]">{label}</span>

          {(isDisconnected || hasIncompleteLeads) && (
            <Tooltip>
              <TooltipTrigger>
                {isDisconnected ? (
                  <WarningCircle className="fill-red-500 size-3.25" />
                ) : (
                  <Info className="fill-red-500 size-3.25" />
                )}
              </TooltipTrigger>

              <TooltipContent className="text-center">
                {isDisconnected
                  ? "Reconnect your account to resume lead collection."
                  : "Some leads couldn't be collected - a few profiles may be private or unavailable."}
              </TooltipContent>
            </Tooltip>
          )}
        </span>

        {state === "COMPLETED" ? (
          <span className="text-button-xxs text-black-500">
            Updated{" "}
            {lastMinedAt
              ? timeSince(new Date(lastMinedAt).getTime())
              : "recently"}
          </span>
        ) : (
          <SourceProgress
            current={processed}
            total={total}
            isDisconnected={isDisconnected}
          />
        )}
      </div>

      <RenderIf condition={state === "COMPLETED"}>
        <div className="flex flex-col gap-0.5 items-end">
          {isLoadingTotal ? (
            <Skeleton className="h-4 w-8" />
          ) : (
            <span className="text-xs-bold">{resultsTotal}</span>
          )}
          <span className="text-button-xxs text-black-400">leads</span>
        </div>
      </RenderIf>

      <div className="absolute group-hover:opacity-100 has-[[data-state=open]]:opacity-100 opacity-0 transition-opacity duration-300 right-0 h-full pl-10 pr-5 top-0 flex justify-end items-center bg-[linear-gradient(270deg,_#F9FAFB_40%,_rgba(249,_250,_251,_0.7)_100%)]">
        <SourceActions listId={listId} sourceId={source.id}>
          <Button
            size="xs"
            variant="secondary-black"
            intent="iconOnly"
            type="button"
            aria-label="More options"
          >
            <DotsThreeVertical />
          </Button>
        </SourceActions>
      </div>
    </div>
  );
}
